import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, IconButton, MenuItem, Select, Typography } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import CloseIcon from '@mui/icons-material/Close';
import { FloatingActionBar } from '../spLot/FloatingBar';
import FullScreenActionLoader from '../common/FullScreenActionLoader';

type TrialMaterialBulkActionsProps = {
    classes: any;
    selectionModel: GridRowSelectionModel;
    clearBulkSelection: () => void;
    handleMarkAsComplete: () => void;
    canMarkSelectedAsComplete: boolean;
    isManualCompleteSubmitting: boolean;
    handleOpenUpdateRemark: () => void;
    isBulkRemarkSubmitting: boolean;
    openRemarkDialog: boolean;
    setOpenRemarkDialog: (open: boolean) => void;
    selectedMaterialIds: string[];
    bulkRemarkError: string;
    selectedBulkRemark: string;
    setSelectedBulkRemark: (value: string) => void;
    setBulkRemarkError: (value: string) => void;
    handleSaveBulkRemark: () => void;
    trialRemarks: any[];
};

export default function TrialMaterialBulkActions({
    classes,
    selectionModel,
    clearBulkSelection,
    handleMarkAsComplete,
    canMarkSelectedAsComplete,
    isManualCompleteSubmitting,
    handleOpenUpdateRemark,
    isBulkRemarkSubmitting,
    openRemarkDialog,
    setOpenRemarkDialog,
    selectedMaterialIds,
    bulkRemarkError,
    selectedBulkRemark,
    setSelectedBulkRemark,
    setBulkRemarkError,
    handleSaveBulkRemark,
    trialRemarks,
}: TrialMaterialBulkActionsProps) {
    const handleCloseRemarkDialog = () => {
        setOpenRemarkDialog(false);
        setSelectedBulkRemark('');
        setBulkRemarkError('');
    };

    return (
        <>
            <FullScreenActionLoader open={isManualCompleteSubmitting || isBulkRemarkSubmitting} />
            <FloatingActionBar
                open={selectionModel.length > 0}
                selectedCount={selectionModel.length}
                type="trial-material"
                hideActions
                onClose={clearBulkSelection}
                customActions={[
                    {
                        label: 'Mark as complete',
                        onClick: handleMarkAsComplete,
                        disabled: !canMarkSelectedAsComplete || isManualCompleteSubmitting,
                    },
                    {
                        label: 'Update remark',
                        onClick: handleOpenUpdateRemark,
                        disabled: isBulkRemarkSubmitting,
                    },
                ]}
            />

            <Dialog
                open={openRemarkDialog}
                onClose={handleCloseRemarkDialog}
                maxWidth="sm"
                fullWidth
                slotProps={{ paper: { sx: { borderRadius: 3, p: 1 } } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Typography className={classes.labelNoto20Bold600}>Update remarks</Typography>
                    <IconButton aria-label="close" onClick={handleCloseRemarkDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography className={classes.labelNoto14Bold500} sx={{ mb: 2 }}>
                        {selectedMaterialIds.length > 0
                            ? `${selectedMaterialIds.length} material${selectedMaterialIds.length > 1 ? 's' : ''} selected`
                            : 'No materials selected'}
                    </Typography>
                    <FormControl fullWidth size="small" error={!!bulkRemarkError}>
                        <Select
                            value={selectedBulkRemark}
                            displayEmpty
                            onChange={(e) => {
                                setSelectedBulkRemark(String(e.target.value ?? ''));
                                setBulkRemarkError('');
                            }}
                        >
                            <MenuItem value="" disabled>Select Remark</MenuItem>
                            {trialRemarks.filter((remark: any) => remark.remark_id !== 8).map((remark: any) => (
                                <MenuItem key={`bulk-remark-${remark?.remark_id}`} value={String(remark?.remark_id ?? '')}>
                                    {remark?.remark_name}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>{bulkRemarkError}</FormHelperText>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="contained" className={classes.whiteButton} onClick={handleCloseRemarkDialog}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        className={classes.greenButton}
                        onClick={handleSaveBulkRemark}
                        disabled={isBulkRemarkSubmitting}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
